import { memo, useState } from "react";
import styled from "styled-components";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import { IDetailTour, IImage } from "@interfaces";
import {
    StyledSlideBannerMobile,
    StyledTourInfo,
    StyledRatio34Image,
} from "@components";
import { HeartTourModule, InfoBoxModule } from "@module";

interface IDetailTourMobileComponent {
    tour: IDetailTour;
    handleClickSupplier?: () => void;
}

export const DetailTourMobileComponent = memo((props: IDetailTourMobileComponent) => {
    const { tour, handleClickSupplier } = props;
    return (
        <>
            <StyledSlideBannerMobile>
                <Carousel infiniteLoop showThumbs={false}>
                    {[
                        ...(!tour.images.some((item: IImage) => item.path === tour.productImagePath)
                            ? [{ mediaId: "productImage", path: tour.productImagePath }]
                            : []),

                        ...tour.images.filter(
                            (item: IImage) => item.path === tour.productImagePath,
                        ),

                        ...tour.images.filter(
                            (item: IImage) => item.path !== tour.productImagePath,
                        ),
                    ].map((item: IImage, index) => (
                        <StyledRatio34Image key={index} img={item.path} />
                    ))}
                </Carousel>
            </StyledSlideBannerMobile>

            <Main>
                <div className="heart">
                    <HeartTourModule productId={tour.productId} />
                </div>
                <StyledTourInfo>
                    <h1 className="title">{tour.name}</h1>
                    <p className="place">
                        <img src="/images/fi_map-pin.png" alt="mapIcon" className="mapIcon" />
                        <span>{tour.location}</span>
                    </p>
                    <hr style={{ marginTop: "16px" }} />
                    <p className="supplier" onClick={handleClickSupplier}>
                        Supplier: <span>{tour.supplier}</span>
                    </p>
                    <p className="tripID">Trip ID: {tour.productId}</p>
                    <p className="tourCode">Tour Code: {tour.tourCode}</p>
                    <hr style={{ margin: "16px 0" }} />
                    <div className="durationNPrice">
                        <div className="duration">
                            <img src="/images/fi_clock.png" alt="fi_clock" />
                            <span>{tour.duration}</span>
                        </div>
                    </div>
                </StyledTourInfo>
            </Main>
        </>
    );
});

interface ITourNeedToKnowMobile {
    data: IDetailTour["description"];
}

/**
 * The mobile "Need to know" accordion, split out of the tour body so the page can
 * render it below the availability panel. It keeps its own open/closed state: no
 * other section reacts to it, so there is nothing to gain from lifting it, and the
 * desktop layout tracks its own copy independently.
 */
export const TourNeedToKnowMobile = ({ data }: ITourNeedToKnowMobile) => {
    const [needToKnowOpen, setNeedToKnowOpen] = useState(false);
    return (
        <NeedToKnowSection>
            <CollapsibleHeader onClick={() => setNeedToKnowOpen((prev) => !prev)}>
                <CollapsibleTitle>Need to know</CollapsibleTitle>
                <CollapseHint>
                    <CollapseHintText>{needToKnowOpen ? "click to collapse" : "click to expand"}</CollapseHintText>
                    <CollapseArrow $isOpen={needToKnowOpen}>
                        <svg viewBox="0 0 24 24" width="20" height="20">
                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
                        </svg>
                    </CollapseArrow>
                </CollapseHint>
            </CollapsibleHeader>
            <CollapsibleBody $isOpen={needToKnowOpen}>
                <InfoBoxModule data={data} />
            </CollapsibleBody>
        </NeedToKnowSection>
    );
};

// Mirrors the white backdrop the accordion had while it sat inside Main.
const NeedToKnowSection = styled.div`
    position: relative;
    background-color: #fff;
`;

const CollapsibleHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    background-color: ${(p) => p.theme.colors.black};
    color: #fff;
    padding: 16px 20px;
    margin: 0 -16px;
`;

const CollapsibleTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
`;

const CollapseHint = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CollapseHintText = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: #fff;
    opacity: 0.8;
`;

const CollapseArrow = styled.span<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    transition: transform 0.25s ease;
    transform: rotate(${(p) => (p.$isOpen ? "180deg" : "0deg")});
    color: #fff;
`;

const CollapsibleBody = styled.div<{ $isOpen: boolean }>`
    max-height: ${(p) => (p.$isOpen ? "2000px" : "0")};
    overflow: hidden;
    transition: max-height 0.35s ease;
`;

const Main = styled.div`
    position: relative;
    background-color: #fff;
    .heart {
        position: absolute;
        top: 12px;
        right: 15px;
    }

`;
